import * as React from 'react';

import { cn, Col, Row, Switch } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideBell,
  LucideCode,
  LucideLoader2,
  LucideLock,
} from 'lucide-react';

import { patchScopedSettingValuesMutationAtom } from '../../_graphql/mutationPatchScopedSettingValues';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import { notificationPreferencesUpdateMutationAtom } from '~/modules/notifications/_graphql/mutationNotificationPreferencesUpdate';
import { notificationPreferencesQueryAtom } from '~/modules/notifications/_graphql/queryNotificationPreferences';
import { notificationTypesQueryAtom } from '~/modules/notifications/_graphql/queryNotificationTypes';
import {
  NotificationPreferenceRecord,
  NotificationTypeRecord,
} from '~/modules/notifications/types';
import { getNotificationIcon } from '~/modules/notifications/utils/notificationTypeIcons';
import { useIdentity } from '~/shared/hooks/useIdentity';
import { $allSettingsGroups } from '~/shared/state/settings.state';

const CHANNELS = ['in_app', 'email', 'push'] as const;

function isChannelEnabled(
  typeKey: string,
  channel: string,
  preferences: NotificationPreferenceRecord[],
  notificationType: NotificationTypeRecord,
): boolean {
  const pref = preferences.find(
    (p) => p.notificationTypeKey === typeKey && p.channel === channel,
  );
  if (pref) return pref.enabled;
  return notificationType.defaultChannels.includes(channel);
}

const SELF_NOTIFY_SETTING_ID = 'notification.selfNotify';

const $notificationTypes = notificationTypesQueryAtom();
const $notificationPreferences = notificationPreferencesQueryAtom();
const $updatePreferences = notificationPreferencesUpdateMutationAtom();
const $patchSelfNotify = patchScopedSettingValuesMutationAtom({
  onSuccess: (get) => {
    get($allSettingsGroups).refetch();
  },
});

export const NotificationsView = () => {
  const t = useTranslations();
  const toast = useToast();
  const { organization } = useIdentity();

  const typesQuery = useAtomValue($notificationTypes);
  const preferencesQuery = useAtomValue($notificationPreferences);
  const settingsGroupsQuery = useAtomValue($allSettingsGroups);
  const updatePreferences = useSetAtom($updatePreferences);
  const patchSelfNotify = useSetAtom($patchSelfNotify);

  const types = typesQuery.data ?? [];
  const preferences = preferencesQuery.data ?? [];

  const selfNotify = React.useMemo(() => {
    const groups = settingsGroupsQuery.data?.settingsGroups ?? [];
    for (const group of groups) {
      const setting = group.settings.find(
        (s) => s.id === SELF_NOTIFY_SETTING_ID,
      );
      if (setting) {
        const orgValue = setting.values.find((v) => v.scope === 'Organization');
        if (orgValue) return orgValue.value === true;
        return setting.defaultValue === true;
      }
    }
    return false;
  }, [settingsGroupsQuery.data]);

  const isLoading =
    typesQuery.loadingState.isInitialLoading ||
    preferencesQuery.loadingState.isInitialLoading;

  const groupedTypes = React.useMemo(() => {
    const groups = new Map<string, NotificationTypeRecord[]>();
    for (const type of types) {
      const existing = groups.get(type.category) ?? [];
      existing.push(type);
      groups.set(type.category, existing);
    }
    return groups;
  }, [types]);

  const handleToggle = React.useCallback(
    async (typeKey: string, channel: string, enabled: boolean) => {
      try {
        await updatePreferences({
          input: [{ notificationTypeKey: typeKey, channel, enabled }],
        });
        preferencesQuery.refetch();
      } catch {
        toast.error(t.notificationsView.messages.saveError);
      }
    },
    [updatePreferences, preferencesQuery, toast, t],
  );

  const handleSelfNotifyToggle = React.useCallback(
    async (value: boolean) => {
      if (!organization) return;

      try {
        await patchSelfNotify({
          scope: 'Organization',
          objectId: organization.id,
          values: { [SELF_NOTIFY_SETTING_ID]: value },
        });
      } catch {
        toast.error(t.notificationsView.messages.saveError);
      }
    },
    [patchSelfNotify, organization, toast, t],
  );

  if (isLoading) {
    return (
      <Col xCenter yCenter className="h-full p-5">
        <LucideLoader2 className="size-6 animate-spin text-neutral/40" />
      </Col>
    );
  }

  const hasError = !!typesQuery.error || !!preferencesQuery.error;

  if (hasError) {
    return (
      <Col xCenter yCenter className="h-full p-5 text-neutral/50">
        <LucideBell className="mb-2 size-8" />
        <span className="text-sm font-medium">
          {t.notificationsView.messages.errorMessage}
        </span>
      </Col>
    );
  }

  if (types.length === 0) {
    return (
      <Col xCenter yCenter className="h-full p-5 text-neutral/50">
        <LucideBell className="mb-2 size-8" />
        <span className="text-sm font-medium">{t.notificationsView.empty}</span>
      </Col>
    );
  }

  return (
    <Col className="@NotificationsView h-full p-5">
      <FormSection icon={LucideBell} title={t.notificationsView.title}>
        <p className="mb-4 text-sm text-neutral/60">
          {t.notificationsView.description}
        </p>

        <div className="max-w-2xl">
          {Array.from(groupedTypes.entries()).map(
            ([category, categoryTypes]) => (
              <div key={category} className="mb-6">
                <h3 className="mb-3 text-xs font-semibold tracking-wider text-neutral/50 uppercase">
                  {t.notificationsView.categories[category] ?? category}
                </h3>

                <div className="rounded-lg border border-neutral/10">
                  <Row className="border-b border-neutral/10 px-4 py-2">
                    <span className="flex-1 text-xs font-medium text-neutral/50">
                      {t.notificationsView.columns.type}
                    </span>
                    {CHANNELS.map((channel) => (
                      <span
                        key={channel}
                        className="w-20 text-center text-xs font-medium text-neutral/50"
                      >
                        {t.notificationsView.channels[channel] ?? channel}
                      </span>
                    ))}
                  </Row>

                  {categoryTypes.map((notifType) => {
                    const TypeIcon = getNotificationIcon(notifType.icon);
                    return (
                      <Row
                        key={notifType.id}
                        yCenter
                        className={cn(
                          'border-b border-neutral/10 px-4 py-3 last:border-b-0',
                        )}
                      >
                        <div className="flex flex-1 items-center gap-2">
                          <TypeIcon className="size-4 flex-shrink-0 text-neutral/40" />
                          <span className="text-sm">
                            {t.notificationsView.typeLabels[notifType.key] ??
                              notifType.key}
                          </span>
                          {notifType.isCritical && (
                            <LucideLock className="size-3 text-neutral/40" />
                          )}
                        </div>
                        {CHANNELS.map((channel) => {
                          const enabled = isChannelEnabled(
                            notifType.key,
                            channel,
                            preferences,
                            notifType,
                          );
                          const isDisabled = notifType.isCritical;

                          return (
                            <div
                              key={channel}
                              className="flex w-20 justify-center"
                            >
                              <Switch
                                isSelected={isDisabled ? true : enabled}
                                isDisabled={isDisabled}
                                onChange={(val) =>
                                  handleToggle(notifType.key, channel, val)
                                }
                              />
                            </div>
                          );
                        })}
                      </Row>
                    );
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      </FormSection>

      <FormSection
        icon={LucideCode}
        title={t.notificationsView.devSettings.title}
      >
        <div className="max-w-2xl">
          <div className="rounded-lg border border-neutral/10">
            <Row yCenter className="px-4 py-3">
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium">
                  {t.notificationsView.devSettings.selfNotify}
                </span>
                <span className="text-xs text-neutral/50">
                  {t.notificationsView.devSettings.selfNotifyDescription}
                </span>
              </div>
              <Switch
                isSelected={selfNotify}
                onChange={handleSelfNotifyToggle}
              />
            </Row>
          </div>
        </div>
      </FormSection>
    </Col>
  );
};
